import { getRegion } from '@/helpers/getRegion';
import { getUrlFromConfig, getWebsocketUrlFromConfig } from '@/utils/endpoints';
import { Region } from '@heidi/schema/ml-scribe';

/** Placeholder in `NEXT_PUBLIC_INBOX_SERVICE_URL` (clinical-coding-style). */
export const INBOX_SERVICE_REGION_PLACEHOLDER = '<REGION>';

/** AWS region codes of deployed inbox-service cells. Unmapped regions get no origin. */
const regionToInboxAwsRegion: Partial<Record<Region, string>> = {
  [Region.UK]: 'euw2',
};

/**
 * Inbox-service origin for `region`, or undefined when it has no cell. A URL
 * without `<REGION>` sends every region to one cell, so production refuses it.
 */
const resolveInboxServiceOrigin = (region: Region): string | undefined => {
  const configured = process.env.NEXT_PUBLIC_INBOX_SERVICE_URL;
  if (!configured?.includes(INBOX_SERVICE_REGION_PLACEHOLDER)) {
    return process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? undefined : configured;
  }

  const awsRegion = regionToInboxAwsRegion[region];
  if (!awsRegion) {
    return undefined;
  }

  return configured.replaceAll(INBOX_SERVICE_REGION_PLACEHOLDER, awsRegion);
};

/**
 * Get domains for the current region.
 * @param region - optional region override, used for SSR (e.g. EHR Launch).
 *   When omitted, delegates to {@link getRegion} for the unified fallback chain.
 */
export const getDomains = (region?: Region) => {
  const finalRegion = region ?? getRegion();

  const baseUrl = getUrlFromConfig(finalRegion);
  const baseWebsocketUrl = getWebsocketUrlFromConfig(finalRegion);

  // Inbox-service origin for the same region as the ml-scribe base URL above. No
  // fallback to the Scribe gateway: with no cell for the region it is undefined,
  // and the inbox client refuses to send (see `inboxHttp`).
  const inboxServiceOrigin = resolveInboxServiceOrigin(finalRegion)?.replace(/\/+$/, '');

  return {
    baseV1Url: baseUrl + '/api/v1/ml-scribe/',
    baseV1IdentityBrokerUrl: baseUrl + '/api/v1/identity-broker/',
    baseV2Url: baseUrl + '/api/v2/ml-scribe/',
    openApiBaseUrl: baseUrl + '/api/v2/ml-scribe/open-api/',
    baseV2_1Url: baseUrl + '/api/v2-1/ml-scribe/',
    baseV2_2Url: baseUrl + '/api/v2-2/ml-scribe/',
    inboxServiceUrl: inboxServiceOrigin ? `${inboxServiceOrigin}/api/v1/` : undefined,
    // Bare origin (no `/api/v1/` suffix) for `api/upload-client.ts`, which
    // builds its own `${baseUrl}/api/v1/...` paths internally.
    inboxServiceOrigin,
    websocketV1Url: baseWebsocketUrl,
  };
};

export const getDomain = (endPointVersion: 'v1' | 'v2' | 'v2-1' | 'v2-2') => {
  const region = getRegion();
  const baseUrl = getUrlFromConfig(region);
  return baseUrl + `/api/${endPointVersion}/ml-scribe/`;
};
